import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Pagination,
  TablePagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BrandOverviewTableSkeleton from "../../BrandSkeletons/BrandOverviewTableSkeleton";
import { Stack } from "@mui/system";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DefinitionsDrawer from "./DefinitionsDrawer";
import { formatMetricName } from "../../utilitiesOfBrandConfig/helperOfBrandConfig";
import { useLocation, useSearchParams } from "react-router-dom";
import { setDisplayErrorSnackBar } from "../../../../../../features/userSlice";
import { setHighlightDatePicker } from "../../../../../../features/brandConfigurationReportSlice";

// Column definitions — sortable: false on all since order is controlled statically by METRIC_ORDER
const tableHeaders = [
  { id: 1, KEY: "destination_field_name", label: "Metric", sortable: false },
  { id: 2, KEY: "rule_description", label: "Rule", sortable: false },
  {
    id: 3,
    KEY: "source_field_expression",
    label: "Affiliate Term",
    sortable: false,
  },
  {
    id: 4,
    KEY: "calculation_type",
    label: "Calculation Type",
    sortable: false,
  },
  { id: 5, KEY: "report_name", label: "Report Name", sortable: false },
  { id: 6, KEY: "definition_json", label: "Definition JSON", sortable: false },
];

interface Props {
  tableHeadings?: { [key: string]: string };
  modifiedTableData?: any[];
  reportType?: string;
}

const AdvisorAndUnifierTable = ({
  tableHeadings,
  modifiedTableData,
}: Props) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";

  const headCells = Object.entries(tableHeadings || {});

  const loaderForSwitchingTabs = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForSwitchingTabs,
  );

  //Brand Account Overview - to Get the Brand name
  const brandAccountOverviewInfo = useSelector(
    (state: any) => state.brandConfigurationReport.brandAccountOverviewInfo,
  );

  // No sorting — order comes directly from modifiedTableData (controlled by METRIC_ORDER in parent)
  useEffect(() => {
    if (!modifiedTableData || modifiedTableData.length === 0) {
      setTableData([]);
      return;
    }
    setTableData(modifiedTableData);
  }, [modifiedTableData]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return tableData.slice(start, start + rowsPerPage);
  }, [tableData, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOpenDefinitionModal = (row: any) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  // Handle Metric value click
  const handleMetricValueClick = (
    e: React.MouseEvent,
    rawTableId: string | any,
    field_mapping_id: string | any,
    metric: string,
    affiliate_term: string | any,
  ) => {
    if (!startDate || !endDate) {
      e.preventDefault(); // block navigation if dates are not selected
      dispatch(setHighlightDatePicker(true));
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "In order to display Raw Data, You must first select a Date Range to display.",
          type: "error",
        }),
      );
      return;
    }

    if (!rawTableId || !field_mapping_id) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unable to navigate: missing table id or field mapping id.",
          type: "error",
        }),
      );
      return;
    }

    const brandName = brandAccountOverviewInfo[0]?.brand_name;

    if (!brandName) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unable to navigate: brand information is unavailable.",
          type: "error",
        }),
      );
      return;
    }

    // Build new URL and open in new tab. This component is shared between
    // the Brands module (operations/brands/...) and the legacy Brand
    // Configuration Report module (operations/brand_configuration_report/...)
    // — route to whichever one the user is currently in.
    const cleanBrandName = brandAccountOverviewInfo[0]?.brand_name
      .trim()
      .replace(/\s+/g, "_");

    const isFromBrandsModule = location.pathname.startsWith("/operations/brands/");
    const pathname = isFromBrandsModule
      ? `/operations/brands/${cleanBrandName}/raw_table_overview`
      : `/operations/brand_configuration_report/${cleanBrandName}_configuration_overview/raw_table_overview`;

    const newParams = new URLSearchParams(location.search);
    newParams.set("rawTableId", rawTableId);
    newParams.set("fieldMappingId", field_mapping_id);
    newParams.set("metric", metric);
    newParams.set("affiliateTerm", affiliate_term);

    window.open(`${pathname}?${newParams.toString()}`, "_blank");
  };

  return (
    <>
      {loaderForSwitchingTabs ? (
        <BrandOverviewTableSkeleton />
      ) : (
        <>
          <TableContainer
            component={Paper}
            className="performanceTable"
            sx={{
              padding: "0px 0px",
              boxShadow: "none",
              borderStartStartRadius: "0px",
              maxHeight: 450,
              overflowX: "auto",
            }}
          >
            <Table aria-label="collapsible table" size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell key={header.KEY} align="left">
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row: any, rowIndex: number) => (
                    <React.Fragment key={rowIndex}>
                      <TableRow
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        {headCells.map(([key], cellIndex) => (
                          <TableCell key={cellIndex} align="left">
                            {key === "destination_field_name" ? (
                              <span
                                style={{
                                  color: row.isAdvisorOnly
                                    ? "#9E9E9E"
                                    : "#9C27B0",
                                  cursor: row.isAdvisorOnly
                                    ? "not-allowed"
                                    : "pointer",
                                  textDecoration: row.isAdvisorOnly
                                    ? "none"
                                    : "underline",
                                  pointerEvents: row.isAdvisorOnly
                                    ? "none"
                                    : "auto",
                                  fontWeight: 500,
                                }}
                                onClick={(e) =>
                                  handleMetricValueClick(
                                    e,
                                    row.raw_table_id,
                                    row.field_mapping_id,
                                    row.destination_field_name
                                      ? formatMetricName(
                                          row.destination_field_name,
                                        )
                                      : "",
                                    row.source_field_expression,
                                  )
                                }
                              >
                                {row[key] ? formatMetricName(row[key]) : ""}
                              </span>
                            ) : key === "definition_json" ? (
                              <Tooltip
                                title={
                                  row.isAdvisorOnly
                                    ? "Definition JSON not available"
                                    : "View Definition JSON"
                                }
                                placement="top"
                              >
                                <IconButton
                                  data-tracker="814"
                                  size="small"
                                  color="secondary"
                                  disabled={row.isAdvisorOnly}
                                  onClick={() => handleOpenDefinitionModal(row)}
                                  aria-label="View definition JSON"
                                >
                                  <DataObjectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              (row[key] ?? "")
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center">
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {tableData.length > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={tableData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                    { display: "none" },
                  ".MuiTablePagination-actions": { display: "none" },
                }}
              />
              <Pagination
                count={Math.ceil(tableData.length / rowsPerPage)}
                page={page + 1}
                onChange={(_, value) => handleChangePage(null, value - 1)}
                showFirstButton
                showLastButton
                className="pagination-button"
                color="secondary"
                size="small"
                sx={{
                  ".css-wjh20t-MuiPagination-ul": { justifyContent: "center" },
                }}
              />
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={tableData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                classes={{ displayedRows: "hidden-displayed-rows" }}
                sx={{
                  ".MuiTablePagination-actions": { display: "none" },
                  ".MuiTablePagination-input": { marginRight: "15px" },
                }}
              />
            </Stack>
          )}
        </>
      )}

      <DefinitionsDrawer
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        row={selectedRow}
      />
    </>
  );
};

export default AdvisorAndUnifierTable;
